<template>
  <div class="audience-list">
    <header class="page-header platform-instagram">
      <div class="header-main">
        <div class="header-icon">
          <img src="../../../assets/platforms/instagram.png" alt="Instagram"/>
        </div>
        <div class="header-text">
          <div class="header-title-row">
            <h1 class="page-title">Instagram audiences</h1>
            <span class="stat-chip">
              <span class="stat-dot"></span>
              <strong>{{ audiences.length }}</strong>{{ hasNextPage ? '+' : '' }} on page
            </span>
            <span class="stat-chip stat-chip-muted" v-if="status !== null">
              {{ getStatusLabel(status) }}
            </span>
          </div>
          <p class="page-subtitle">Manage audience snapshots collected for Instagram profiles.</p>
        </div>
      </div>
    </header>

    <div class="filters-card">
      <div class="filter-label-row">
        <span class="filter-label">Status</span>
        <button class="reset-link" v-if="status !== null" @click="status = null">Reset</button>
      </div>
      <div class="status-tabs">
        <button
          v-for="opt in statusOptions"
          :key="String(opt.value)"
          class="tab"
          :class="['tab-' + opt.tone, { 'is-active': status === opt.value }]"
          @click="status = opt.value"
        >
          <span class="tab-dot" :class="'dot-' + opt.tone"></span>
          {{ opt.label }}
        </button>
      </div>
    </div>

    <div class="table-card" :class="{ 'is-loading': loading }">
      <div class="table-wrap" v-if="!loading && audiences.length">
        <table class="data-table">
          <thead>
            <tr>
              <th class="col-id">#</th>
              <th>Profile</th>
              <th>Status</th>
              <th>Followers collected</th>
              <th class="col-actions"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(audience, $index) in audiences" :key="audience.id || $index">
              <td class="col-id">
                <span class="id-badge">{{ rangeStart + $index }}</span>
              </td>
              <td>
                <a class="profile-link" :href="'https://instagram.com/' + audience.username" target="_blank">
                  <img class="profile-icon" src="../../../assets/platforms/instagram.png"/>
                  <span class="profile-username">@{{ audience.username }}</span>
                  <i class="bi bi-box-arrow-up-right ext-icon" aria-hidden="true"></i>
                </a>
              </td>
              <td>
                <span v-if="audience.is_collected" class="status-pill tone-warning">
                  <span class="pill-dot"></span> Manual
                </span>
                <span v-else class="status-pill" :class="'tone-' + statusTone(audience.status)">
                  <span class="pill-dot"></span> {{ getStatusById(audience.status) }}
                </span>
              </td>
              <td>
                <span class="followers-count">{{ formatNumber(audience.collected_follower_count) }}</span>
                <span class="followers-suffix"> collected</span>
              </td>
              <td class="col-actions">
                <button
                  class="action-btn action-promote"
                  v-if="audience.status === 5"
                  @click="changeStatus($index, audience.instagram_id, 0)"
                  title="Move to IT processing"
                >
                  <i class="bi bi-arrow-right-circle" aria-hidden="true"></i>
                  Move
                </button>
                <a
                  class="action-btn action-edit"
                  :href="'#/audience/create/' + audience.instagram_id + '/' + audience.username"
                  target="_blank"
                  title="Edit audience"
                >
                  <i class="bi bi-pencil-square" aria-hidden="true"></i>
                </a>
                <button
                  class="action-btn action-delete"
                  v-if="audience.status === 5"
                  @click="confirmDelete($index, audience.id, audience.username)"
                  title="Delete"
                >
                  <i class="bi bi-trash3" aria-hidden="true"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="skeleton-list" v-if="loading">
        <div class="skeleton-row" v-for="n in 6" :key="'sk' + n">
          <div class="skeleton skeleton-id"></div>
          <div class="skeleton skeleton-line"></div>
          <div class="skeleton skeleton-pill"></div>
          <div class="skeleton skeleton-line"></div>
          <div class="skeleton skeleton-btn"></div>
        </div>
      </div>

      <div class="empty-state" v-if="!loading && !audiences.length">
        <i class="bi bi-bag" style="font-size:44px" aria-hidden="true"></i>
        <h3>No audiences match this filter</h3>
        <p v-if="status !== null">Try a different status or reset the filter.</p>
        <p v-else>Add an audience from an influencer's profile page.</p>
        <button v-if="status !== null" class="btn btn-outline-secondary" @click="status = null">Show all</button>
      </div>
    </div>

    <div class="pagination-bar" v-if="audiences.length || page > 1">
      <div class="pagination-left">
        <label class="per-page-label">Per page</label>
        <select class="form-control per-page" v-model.number="limit">
          <option :value="25">25</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
          <option :value="200">200</option>
        </select>
      </div>
      <div class="pagination-center">
        <button class="page-btn" @click="goToPage(page - 1)" :disabled="page <= 1">
          <i class="bi bi-chevron-left" aria-hidden="true"></i>
          Previous
        </button>
        <span class="page-indicator">Page <strong>{{ page }}</strong></span>
        <button class="page-btn" @click="goToPage(page + 1)" :disabled="!hasNextPage">
          Next
          <i class="bi bi-chevron-right" aria-hidden="true"></i>
        </button>
      </div>
      <div class="pagination-right"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: "audience-list-instagram",
  data() {
    return {
      audiences: [],
      limit: 50,
      page: 1,
      status: null,
      loading: false,
      apiHost: this.$appSettings.getSetting('host'),
      statusOptions: [
        { value: null, label: 'All', tone: 'neutral' },
        { value: 0, label: 'Migrating to IT', tone: 'neutral' },
        { value: 5, label: 'Downloading', tone: 'info' },
        { value: 10, label: 'Done', tone: 'success' },
        { value: 15, label: 'Error', tone: 'danger' },
        { value: 30, label: 'Automatic', tone: 'purple' }
      ]
    }
  },
  computed: {
    rangeStart() {
      return (this.page - 1) * this.limit + 1;
    },
    hasNextPage() {
      return this.audiences.length === this.limit;
    }
  },
  watch: {
    status() {
      this.page = 1;
      this.getAudiences();
    },
    limit() {
      this.page = 1;
      this.getAudiences();
    }
  },
  methods: {
    getAudiences() {
      this.loading = true;
      const params = {
        limit: this.limit,
        offset: (this.page - 1) * this.limit,
        token: this.$auth.getToken()
      };
      if (this.status !== null) {
        params.status = this.status;
      }
      this.$http.get(this.apiHost + 'audience/list', { params: params }).then(response => {
        this.loading = false;
        this.audiences = response.body.list || [];
      }, () => {
        this.loading = false;
      })
    },
    goToPage(p) {
      if (p < 1) return;
      if (p > this.page && !this.hasNextPage) return;
      this.page = p;
      this.getAudiences();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    confirmDelete($idx, id, username) {
      if (!confirm('Delete audience for @' + username + '?')) return;
      this.deleteAudience($idx, id);
    },
    deleteAudience($idx, id) {
      this.$http.delete(this.apiHost + 'audience/remove/' + id, {
        params: { token: this.$auth.getToken() }
      }).then(() => {
        this.audiences.splice($idx, 1);
        this.$notify({ group: 'default', type: 'success', text: 'Audience deleted' });
      })
    },
    changeStatus($idx, instagramId, status) {
      this.$http.put(this.apiHost + 'audience/change/' + instagramId + '/status/' + status, {}, {
        params: { token: this.$auth.getToken() }
      }).then(() => {
        this.audiences[$idx].status = status;
        this.$notify({ group: 'default', type: 'success', text: 'Status changed' });
      });
    },
    getStatusById(id) {
      const opt = this.statusOptions.find(o => o.value === id);
      return opt ? opt.label : 'Unset';
    },
    getStatusLabel(value) {
      const opt = this.statusOptions.find(o => o.value === value);
      return opt ? opt.label : '';
    },
    statusTone(id) {
      const opt = this.statusOptions.find(o => o.value === id);
      return opt ? opt.tone : 'neutral';
    },
    formatNumber(n) {
      if (n == null) return '0';
      return Number(n).toLocaleString();
    }
  },
  created() {
    this.getAudiences();
  }
}
</script>

<style scoped>
  .audience-list {
    max-width: 1180px;
    margin: 0 auto;
    color: #1f2937;
  }

  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 20px;
    margin-bottom: 22px;
    padding: 22px 24px;
    background: linear-gradient(135deg, #fdf4ff 0%, #ffffff 60%);
    border: 1px solid #f5d0fe;
    border-radius: 16px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
    position: relative;
    overflow: hidden;
  }
  .header-main {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    flex: 1;
  }
  .header-icon {
    flex: 0 0 48px;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: #fdf2f8;
    color: #e1306c;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .header-icon img {
    width: 28px;
    height: 28px;
  }
  .header-title-row {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 6px;
  }
  .page-title {
    font-size: 26px;
    font-weight: 700;
    letter-spacing: -0.02em;
    margin: 0;
    color: #0f172a;
    line-height: 1.1;
  }
  .stat-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #fff;
    border: 1px solid #e5e7eb;
    color: #475569;
    border-radius: 999px;
    padding: 3px 10px 3px 8px;
    font-size: 12px;
    font-weight: 500;
  }
  .stat-chip strong { color: #0f172a; font-weight: 700; }
  .stat-chip-muted {
    background: #fef3c7;
    border-color: #fde68a;
    color: #92400e;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-size: 10.5px;
    font-weight: 700;
    padding: 3px 10px;
  }
  .stat-dot {
    width: 6px; height: 6px;
    border-radius: 50%;
    background: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.18);
  }
  .page-subtitle {
    font-size: 13px;
    color: #6b7280;
    margin: 0;
  }

  .filters-card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    padding: 14px 18px;
    margin-bottom: 18px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  }
  .filter-label-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
  }
  .filter-label {
    font-size: 11px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  .reset-link {
    background: transparent;
    border: 0;
    color: #265a88;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    padding: 2px 8px;
    border-radius: 6px;
  }
  .reset-link:hover {
    background: #eaf1f9;
  }
  .status-tabs {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
  }
  .tab {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #fff;
    border: 1px solid #e5e7eb;
    color: #475569;
    border-radius: 999px;
    padding: 6px 14px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: border-color 0.15s, color 0.15s, background 0.15s, box-shadow 0.15s;
  }
  .tab:hover {
    border-color: #265a88;
    color: #265a88;
  }
  .tab.is-active {
    background: #265a88;
    border-color: #265a88;
    color: #fff;
    box-shadow: 0 2px 6px rgba(38, 90, 136, 0.25);
  }
  .tab.is-active .tab-dot { background: #fff; box-shadow: none; }
  .tab-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
  }
  .dot-neutral { background: #94a3b8; }
  .dot-info { background: #3b82f6; }
  .dot-success { background: #10b981; }
  .dot-danger { background: #ef4444; }
  .dot-purple { background: #a855f7; }
  .dot-warning { background: #f59e0b; }

  .table-card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  }
  .table-wrap { overflow-x: auto; }
  .data-table {
    width: 100%;
    border-collapse: collapse;
  }
  .data-table thead th {
    background: #fafbfc;
    border-bottom: 1px solid #e5e7eb;
    text-align: left;
    padding: 12px 16px;
    font-size: 11.5px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  .data-table tbody td {
    padding: 14px 16px;
    border-bottom: 1px solid #f1f5f9;
    font-size: 14px;
    vertical-align: middle;
  }
  .data-table tbody tr:last-child td { border-bottom: 0; }
  .data-table tbody tr:hover { background: #fafbfc; }
  .col-id { width: 70px; }
  .col-actions { width: 200px; text-align: right; }

  .id-badge {
    display: inline-block;
    background: #f1f5f9;
    color: #475569;
    font-size: 12px;
    font-weight: 600;
    padding: 3px 8px;
    border-radius: 6px;
  }
  .profile-link {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #0f172a;
    font-weight: 600;
    text-decoration: none;
    font-size: 14px;
  }
  .profile-link:hover .profile-username {
    color: #265a88;
    text-decoration: underline;
  }
  .profile-icon {
    width: 22px;
    height: 22px;
    border-radius: 6px;
  }
  .ext-icon {
    color: #6b7280;
    opacity: 0;
    transition: opacity 0.15s;
  }
  .profile-link:hover .ext-icon { opacity: 1; }

  .status-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px 4px 8px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    border: 1px solid transparent;
  }
  .pill-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }
  .tone-neutral { background: #f1f5f9; color: #475569; border-color: #e2e8f0; }
  .tone-info { background: #dbeafe; color: #1d4ed8; border-color: #bfdbfe; }
  .tone-success { background: #d1fae5; color: #065f46; border-color: #a7f3d0; }
  .tone-danger { background: #fee2e2; color: #991b1b; border-color: #fecaca; }
  .tone-purple { background: #f3e8ff; color: #6b21a8; border-color: #e9d5ff; }
  .tone-warning { background: #fef3c7; color: #92400e; border-color: #fde68a; }

  .followers-count {
    font-weight: 600;
    color: #0f172a;
  }
  .followers-suffix {
    color: #6b7280;
    font-size: 12.5px;
  }

  .action-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    background: #fff;
    border: 1px solid #e5e7eb;
    color: #475569;
    border-radius: 8px;
    font-size: 12.5px;
    font-weight: 500;
    cursor: pointer;
    text-decoration: none;
    transition: border-color 0.15s, color 0.15s, background 0.15s;
  }
  .action-btn + .action-btn { margin-left: 4px; }
  .action-btn:hover { border-color: #265a88; color: #265a88; }
  .action-promote {
    background: #eaf1f9;
    border-color: #c7dcf0;
    color: #265a88;
  }
  .action-promote:hover {
    background: #265a88;
    color: #fff;
    border-color: #265a88;
  }
  .action-delete:hover {
    border-color: #dc2626;
    color: #dc2626;
    background: #fef2f2;
  }

  .skeleton-list { padding: 8px 0; }
  .skeleton-row {
    display: grid;
    grid-template-columns: 70px 1fr 140px 1fr 200px;
    gap: 16px;
    padding: 14px 16px;
    border-bottom: 1px solid #f1f5f9;
    align-items: center;
  }
  .skeleton {
    background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
    background-size: 200% 100%;
    animation: shimmer 1.4s infinite;
    border-radius: 6px;
  }
  .skeleton-id { width: 50px; height: 22px; }
  .skeleton-line { height: 14px; width: 70%; }
  .skeleton-pill { width: 110px; height: 26px; border-radius: 999px; }
  .skeleton-btn { width: 100px; height: 28px; justify-self: end; }
  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }

  .empty-state {
    padding: 50px 20px;
    text-align: center;
    color: #6b7280;
  }
  .empty-state .bi { margin-bottom: 12px; }
  .empty-state h3 {
    font-size: 15px;
    font-weight: 600;
    color: #0f172a;
    margin: 0 0 6px;
  }
  .empty-state p {
    margin: 0 0 16px;
    font-size: 13.5px;
  }

  .pagination-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 16px 4px;
  }
  .pagination-left, .pagination-right {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    font-size: 13px;
    color: #6b7280;
  }
  .pagination-right { justify-content: flex-end; }
  .per-page {
    width: auto;
    min-width: 70px;
    padding: 6px 28px 6px 10px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 13px;
    background: #fff;
  }
  .pagination-center {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .page-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #fff;
    border: 1px solid #e5e7eb;
    color: #374151;
    font-size: 13px;
    font-weight: 500;
    padding: 6px 12px;
    border-radius: 8px;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s, color 0.15s;
  }
  .page-btn:hover:not(:disabled) {
    border-color: #265a88;
    color: #265a88;
  }
  .page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
  .page-indicator { font-size: 13px; color: #6b7280; padding: 0 8px; }
  .page-indicator strong { color: #0f172a; font-weight: 600; }

  @media (max-width: 768px) {
    .page-header { padding: 18px; }
    .data-table thead { display: none; }
    .data-table tbody tr { display: grid; grid-template-columns: auto 1fr; gap: 6px 12px; padding: 12px 14px; }
    .data-table tbody td { border-bottom: 0; padding: 4px 0; }
    .col-actions { grid-column: 1 / -1; text-align: left; }
  }
</style>



// WEBPACK FOOTER //
// src/components/audience/instagram/AudienceList.vue